import axios from "axios";
import React, { useState, useEffect } from "react";
import { getCurrentDate, getCurrentMonthFirstDate } from "../../Utilitie";
import toast from "react-hot-toast";
import NoDataFound from "../../Extra/NoDataFound";
import Loading from "../../Extra/Loading";

const tableHead = [
  "Sr",
  "Date",
  "Machine",
  "Section",
  "From",
  "To",
  "Cause",
  "Action",
];

/* --------------------------------------------------------------- */
/*  handleInputChange – preserves all existing fields               */
/* --------------------------------------------------------------- */
const handleInputChange = (asset, date, field, value, setNewEntries) => {
  setNewEntries((prev) => ({
    ...prev,
    [asset]: {
      ...prev[asset],
      [date]: {
        ...(prev[asset]?.[date] || {}),
        [field]: value,
      },
    },
  }));
};

/* --------------------------------------------------------------- */
/*  Helper: has user changed anything in this row?                  */
/* --------------------------------------------------------------- */
const hasChanges = (entry, match) => {
  if (!entry) return false;
  return (
    entry.SECTION_NAME ||
    entry.BLOCK_SECTION_FROM ||
    entry.BLOCK_SECTION_TO ||
    entry.CAUSE
  );
};

const NonWorkingInsert = ({
  section,
  machineData,
  filteredReason,
  loading,
}) => {
  const urlBlockPrefix = process.env.REACT_APP_URL_BLOCK_PREFIX;
  const [startDate, setStartDate] = useState(getCurrentMonthFirstDate());
  const [endDate, setEndDate] = useState(getCurrentDate());
  const [loading2, setLoading2] = useState(false);
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [newEntries, setNewEntries] = useState({});
  const [activeBtn, setActiveBtn] = useState({});
  const [filterData, setFilteredData] = useState([]);
  const [dateRange, setDateRange] = useState([]);

  const sections = [...new Set(section.map((e) => e.section))];
  const causes = [...filteredReason.map((o) => o.table_option_for_insertion)];

  const fetchNonWorkingData = async () => {
    setLoading2(true);
    try {
      const response = await axios.get(
        `${urlBlockPrefix}getNonWorkingData?start=${startDate}&end=${endDate}`
      );
      if (response.data.status === "ok") {
        setData(response.data.daterange.data);
      }
      setLoading2(false);
    } catch (err) {
      console.error(err);
      setLoading2(false);
    }
  };

  const [data, setData] = useState([]);

  useEffect(() => {
    fetchNonWorkingData();
  }, []);

  useEffect(() => {
    const defaultAsset = machineData.find((m) => m.ASSET === "BCM");
    if (defaultAsset) {
      const descriptions = defaultAsset.data.map(
        (item) => item.ASSET_DISCRIPTION
      );
      setFilteredAssets(descriptions);
    }
  }, [machineData]);

  const handleClick = (assetData, btn) => {
    const descriptions = assetData.map((item) => item.ASSET_DISCRIPTION);
    setFilteredAssets(descriptions);
    setActiveBtn({ main: btn, sub: descriptions[0] });
  };

  /* --------------------------------------------------------------- */
  /*  Submit – only send changed fields                               */
  /* --------------------------------------------------------------- */
  const handleSubmit = async (
    asset,
    machineName,
    date,
    isUpdate = false,
    id
  ) => {
    const entry = newEntries[asset]?.[date] || {};
    const match = filterData.find(
      (f) => f.MACHINE_DATE && f.MACHINE_DATE.split("T")[0] === date
    );

    // Build payload with only changed fields
    const changedFields = {};

    if (entry.SECTION_NAME !== undefined)
      changedFields.SECTION_NAME = entry.SECTION_NAME;
    if (entry.BLOCK_SECTION_FROM !== undefined)
      changedFields.BLOCK_SECTION_FROM = entry.BLOCK_SECTION_FROM;
    if (entry.BLOCK_SECTION_TO !== undefined)
      changedFields.BLOCK_SECTION_TO = entry.BLOCK_SECTION_TO;
    if (entry.CAUSE !== undefined) changedFields.CAUSE = entry.CAUSE;

    // For insert, send all required fields (empty string if not set)
    if (!isUpdate) {
      changedFields.MACHINE_NAME = machineName;
      changedFields.MACHINE_DATE = date;
      changedFields.SECTION_NAME = changedFields.SECTION_NAME || "";
      changedFields.BLOCK_SECTION_FROM = changedFields.BLOCK_SECTION_FROM || "";
      changedFields.BLOCK_SECTION_TO = changedFields.BLOCK_SECTION_TO || "";
      changedFields.CAUSE = changedFields.CAUSE || "";
    }

    if (isUpdate && Object.keys(changedFields).length === 0) {
      toast.info("No changes to update.");
      return;
    }

    const payload = isUpdate ? [{ ...changedFields, ID: id }] : [changedFields];

    try {
      const response = await fetch(
        `${urlBlockPrefix}${
          isUpdate ? "updateNonWorkingData" : "insertNonWorkingData"
        }`,
        {
          method: isUpdate ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Failed");

      toast.success(`Data ${isUpdate ? "updated" : "inserted"} successfully!`);

      // Clear only this row's newEntries
      setNewEntries((prev) => ({
        ...prev,
        [asset]: { ...prev[asset], [date]: {} },
      }));

      await fetchNonWorkingData();
    } catch (error) {
      toast.error(`Failed to ${isUpdate ? "update" : "insert"}.`);
      console.error(error);
    }
  };

  const assetTypes = ["BCM", "PQRS", "TRT"];

  useEffect(() => {
    const fData = data.filter((obj) => obj.MACHINE_NAME === activeBtn.sub);
    setFilteredData(fData);
  }, [activeBtn, data]);

  useEffect(() => {
    const dates = [];
    let current = new Date(startDate);
    const end = new Date(endDate);
    while (current <= end) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    setDateRange(dates);
  }, [startDate, endDate]);

  const getCurrentSection = (dateStr, match) => {
    return (
      newEntries[activeBtn.sub]?.[dateStr]?.SECTION_NAME ||
      match?.SECTION_NAME ||
      ""
    );
  };

  const getStationsForSection = (sectionName) => {
    return [
      ...new Set(
        section.filter((e) => e.section === sectionName).map((e) => e.station)
      ),
    ].filter(Boolean);
  };

  return (
    <div>
      {loading && loading2 && <Loading />}
      <div className="d-flex justify-content-between bg-dark p-1 text-white">
        <h5 className="ms-1"> Insert Data </h5>
        <div>
          <b>From </b>
          <input
            type="date"
            value={startDate}
            max={endDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="me-1"
          />
          <b>To </b>
          <input
            type="date"
            value={endDate}
            max={getCurrentDate()}
            onChange={(e) => setEndDate(e.target.value)}
          />
          <button
            className="ms-1 btn btn-sm btn-outline-light"
            onClick={fetchNonWorkingData}
          >
            Fetch
          </button>
        </div>
      </div>

      <div className="papaDiv">
        <div className="papaDiv">
          <h6 className="bg-dark text-white p-1"> Select Machine Type</h6>
          {machineData
            .filter((o) => assetTypes.includes(o.ASSET))
            .map((o) => (
              <button
                key={o.ASSET}
                className={`btn btn-sm btn-outline-dark Subject me-2 ${
                  o.ASSET === activeBtn.main && "active"
                }`}
                onClick={() => handleClick(o.data, o.ASSET)}
              >
                {o.ASSET}
              </button>
            ))}
          <hr />
          {activeBtn?.main && (
            <div>
              <h6 className="bg-dark text-white p-1">
                {" "}
                Select Sub Machine Type
              </h6>
              {filteredAssets.map((subType, i) => (
                <button
                  key={i}
                  className={`btn btn-sm btn-outline-dark Subject me-2 ${
                    subType === activeBtn?.sub && "active"
                  }`}
                  onClick={() => setActiveBtn({ ...activeBtn, sub: subType })}
                >
                  {subType}
                </button>
              ))}
            </div>
          )}
        </div>

        {activeBtn.main && activeBtn.sub && (
          <div className="papaDiv container">
            <div className="Table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr className="table-dark text-white">
                    {tableHead.map((head, i) => (
                      <th
                        key={i}
                        style={{ width: head === "Sr" ? "50px" : "250px" }}
                      >
                        {head}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dateRange.length > 0 ? (
                    dateRange.map((date, i) => {
                      const dateStr = date.toISOString().split("T")[0];
                      const match = filterData.find(
                        (f) =>
                          f.MACHINE_DATE &&
                          f.MACHINE_DATE.split("T")[0] === dateStr
                      );
                      const entry = newEntries[activeBtn.sub]?.[dateStr];
                      const currentSection = getCurrentSection(dateStr, match);
                      const stations = getStationsForSection(currentSection);
                      const changed = hasChanges(entry, match);

                      return (
                        <tr
                          key={i}
                          className={`table-${match ? "success" : "primary"}`}
                        >
                          <td>{i + 1}</td>
                          <td>{dateStr}</td>
                          <td>{activeBtn.sub || "N/A"}</td>

                          {/* SECTION */}
                          <td>
                            <select
                              className="form-control"
                              value={
                                entry?.SECTION_NAME ?? match?.SECTION_NAME ?? ""
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  activeBtn.sub,
                                  dateStr,
                                  "SECTION_NAME",
                                  e.target.value,
                                  setNewEntries
                                )
                              }
                            >
                              <option value="">Select Section</option>
                              {sections.map((sec, idx) => (
                                <option key={idx} value={sec}>
                                  {sec}
                                </option>
                              ))}
                            </select>
                          </td>

                          {/* FROM */}
                          <td>
                            <select
                              className="form-control"
                              value={
                                entry?.BLOCK_SECTION_FROM ??
                                match?.BLOCK_SECTION_FROM ??
                                ""
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  activeBtn.sub,
                                  dateStr,
                                  "BLOCK_SECTION_FROM",
                                  e.target.value,
                                  setNewEntries
                                )
                              }
                            >
                              <option value="">From Station</option>
                              {stations.map((s, si) => (
                                <option key={si} value={s}>
                                  {s}
                                </option>
                              ))}
                            </select>
                          </td>

                          {/* TO */}
                          <td>
                            <select
                              className="form-control"
                              value={
                                entry?.BLOCK_SECTION_TO ??
                                match?.BLOCK_SECTION_TO ??
                                ""
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  activeBtn.sub,
                                  dateStr,
                                  "BLOCK_SECTION_TO",
                                  e.target.value,
                                  setNewEntries
                                )
                              }
                            >
                              <option value="">To Station</option>
                              {stations.map((s, si) => (
                                <option key={si} value={s}>
                                  {s}
                                </option>
                              ))}
                            </select>
                          </td>

                          {/* CAUSE */}
                          <td>
                            <select
                              className="form-control"
                              value={entry?.CAUSE ?? match?.CAUSE ?? ""}
                              onChange={(e) =>
                                handleInputChange(
                                  activeBtn.sub,
                                  dateStr,
                                  "CAUSE",
                                  e.target.value,
                                  setNewEntries
                                )
                              }
                            >
                              <option value="">Select Cause</option>
                              {causes.map((c, idx) => (
                                <option key={idx} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                          </td>

                          {/* ACTION */}
                          <td>
                            {match ? (
                              <button
                                className="btn btn-sm btn-outline-success Subject"
                                onClick={() =>
                                  handleSubmit(
                                    activeBtn.sub,
                                    activeBtn.sub,
                                    dateStr,
                                    true,
                                    match.ID
                                  )
                                }
                                disabled={!changed}
                              >
                                Update
                              </button>
                            ) : (
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() =>
                                  handleSubmit(
                                    activeBtn.sub,
                                    activeBtn.sub,
                                    dateStr
                                  )
                                }
                                disabled={!changed}
                              >
                                Insert
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={tableHead.length}>
                        <NoDataFound />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NonWorkingInsert;
